import { CellClickedEvent } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css'; // Core CSS
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import { GroupCellRendererParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { Tag, Text, Tooltip } from 'components/ui';
import { format, formatDistanceToNow } from 'date-fns';
import { getListOfNetworks } from 'lib/marketplace-utils';

import { useMutation } from '@tanstack/react-query';
import { SizeColumnsToFitGridStrategy } from 'ag-grid-community/dist/lib/interfaces/autoSizeStrategy';
import CreateListingDialog from 'components/create-listing-dialog/create-listing-dialog';
import { useDialogToggle } from 'hooks/use-dialog-toggle';
import { useMarketplaceConfig } from 'hooks/use-marketplace-config';
import {
  FlexContainer,
  buySellCellRenderer,
  getArrayItem,
  linkCellRenderer,
  numberCellRenderer,
  productTypeCellRenderer,
  stringCellRenderer,
} from 'lib/ag-grid-helpers';
import { ApiClient } from 'lib/api/client';
import { ListingBookmark, ListingRead, ProductTypes } from 'lib/api/generated';
import {
  formatAUD,
  formatListingDateRange,
  formattedSoldPrices,
} from 'lib/helpers';
import { ColumnDef, hasValue } from 'lib/marketplace-utils';
import { useStore } from 'lib/store';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { DATE_FORMAT, PATHS } from 'settings/config';
import { buildPublicListingPath } from 'lib/listing-utils';
import styled from 'styled-components';
import { breakpoints } from 'styles/breakpoints';
import { globalTheme } from 'styles/global-theme';
import { PriceTypeEnum } from '../../../components/marketplace-listing/listing-form-schema';
import {
  PriceFilter,
  ProductFilter,
  SearchFilter,
  YearFilter,
} from '../../../components/marketplace/filters';
import priceTypeFilter from '../../../components/marketplace/filters/priceTypeFilter';

interface MarketplaceTableProps {
  listingData?: any;
  refetch?: any;
}

const GridWrapper = styled.div`
  padding-left: ${globalTheme.space[5]};
  padding-right: ${globalTheme.space[5]};
  padding-bottom: ${globalTheme.space[5]};
  display: flex;
  position: relative;
  flex-direction: column;
  @media screen and (max-width: ${breakpoints.medium}) {
    padding: ${globalTheme.space[1]};
  }
`;
const ActiveStatus = styled.div<{ status: 'active' | 'filled' }>`
  width: 10px;
  height: 10px;
  background-color: ${(props) =>
    props.status === 'active'
      ? globalTheme.colors.green
      : globalTheme.colors.red};
  border-radius: 100%;
`;

const MarketplaceTableView = ({
  listingData,
  refetch,
}: MarketplaceTableProps) => {
  const router = useRouter();
  const [columnDefs, setColumnDefs] = useState<ColumnDef[]>([]);
  const perPage = 20;
  const noRowsTemplate = `<span class="ag-overlay-loading-center" style="padding: 10px;  background: #55AA77 color:#ffffff";>No records to show</span>`;
  const loadingTemplate = `<span class="ag-overlay-loading-center" style="padding: 10px;  background: #55AA77 color:#ffffff";>Loading</span>`;
  const { data: marketplaceConfig, isLoading: isMarketplaceConfigLoading } =
    useMarketplaceConfig();
  const { data: session } = useSession();
  const stateFilter = useStore((store) => store?.filter);

  const [
    createListingDialogOpen,
    openCreateListingDialog,
    closeCreateListingDialog,
  ] = useDialogToggle(false);

  const bookmarkListingMutation = useMutation(
    (listingDetail: { listingId: string; listingBookmark: ListingBookmark }) =>
      new ApiClient().marketplacePublicListingApi.marketplaceListingBookmarkCreate(
        {
          listingId: listingDetail.listingId,
          listingBookmarkRequest: listingDetail.listingBookmark,
        }
      ),

    {
      onSuccess: () => {
        refetch?.();
        toast.success(`Status updated sucessfully`);
      },
      onError: () => {
        toast.error(`Failed to update status`);
      },
    }
  );

  const handleFilter = () => {
    const queryString = Object.entries(stateFilter)
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return `${key}=${value.map((v) => encodeURIComponent(v)).join(',')}`;
        }
        return `${key}=${encodeURIComponent(value as string)}`;
      })
      .join('&');

    router.push({
      pathname: '/marketplace',
      search: queryString,
    });
  };

  useEffect(() => {
    handleFilter();
  }, [stateFilter]);

  const handleBookmarkChange = (
    listingId: string,
    bookmark: ListingBookmark
  ) => {
    bookmarkListingMutation.mutateAsync({
      listingId: listingId,
      listingBookmark: bookmark,
    });
  };

  function getPriceType(getPriceType: string): string {
    switch (getPriceType) {
      case 'PER_HEAD':
        return 'Head';
      case 'LIVEWEIGHT':
        return 'Liveweight';
      case 'HSCW':
        return 'HSCW';
      default:
        return '';
    }
  }

  const priceRenderer = (price: { min: string; max: string }): string => {
    if (price.min === price.max) {
      return formatAUD(price.min);
    } else {
      return `${formatAUD(price.min)} - ${formatAUD(price.max)}`;
    }
  };

  useEffect(() => {
    let colData: ColumnDef[] = [
      {
        field: 'type',
        headerName: 'Type',
        filter: 'agSetColumnFilter',
        cellRenderer: (params: GroupCellRendererParams) =>
          buySellCellRenderer(params),
      },
      {
        field: 'product',
        headerName: 'Commodity',
        filter: ProductFilter,
        cellRenderer: (params: GroupCellRendererParams) =>
          productTypeCellRenderer(params),
      },
      {
        field: 'status',
        headerName: 'Status',
        filter: 'agSetColumnFilter',
        cellRenderer: (params: GroupCellRendererParams) => (
          <FlexContainer>
            {params.value === 'Filled' ? (
              <>
                <ActiveStatus status='filled' />
                <Text>Filled</Text>{' '}
              </>
            ) : (
              <>
                <ActiveStatus status='active' />
                <Text>Active</Text>
              </>
            )}
          </FlexContainer>
        ),
      },
      {
        field: 'visibility',
        headerName: 'Visibility',
        filter: 'agSetColumnFilter',
        cellRenderer: (params: GroupCellRendererParams) => (
          <FlexContainer>
            {params.value === 'PRIVATE' ? (
              <Tag color='white' backgroundColor='red'>
                Private
              </Tag>
            ) : (
              <Tag color='white' backgroundColor='darkBlue'>
                Public
              </Tag>
            )}
          </FlexContainer>
        ),
      },
      {
        field: 'anonymous',
        headerName: 'Anonymous',
        filter: 'agSetColumnFilter',
        cellRenderer: (params: GroupCellRendererParams) => {
          return (
            <FlexContainer>
              {params.value && (
                <Tag color='pink' backgroundColor='lightPink'>
                  Anonymous
                </Tag>
              )}
            </FlexContainer>
          );
        },
      },
      {
        field: 'quantity_min',
        headerName: 'Quantity (Min)',
        filter: 'agNumberColumnFilter',
        filterParams: {
          maxNumConditions: 1,
        },
        cellRenderer: (params: GroupCellRendererParams) =>
          numberCellRenderer(params),
      },
      {
        field: 'quantity_max',
        headerName: 'Quantity (Max)',
        filter: 'agNumberColumnFilter',
        filterParams: {
          maxNumConditions: 1,
        },
        cellRenderer: (params: GroupCellRendererParams) =>
          numberCellRenderer(params),
      },
      {
        field: 'stock_ages',
        headerName: 'Age',
        filter: 'agSetColumnFilter',
        cellRenderer: (params: GroupCellRendererParams) => (
          <FlexContainer>
            {params.value && params.value.length ? (
              <Tooltip label={params.value.join(',')}>
                <Text>{params.value.join(', ')}</Text>
              </Tooltip>
            ) : (
              <Text>-</Text>
            )}
          </FlexContainer>
        ),
      },
      {
        field: 'stock_gender',
        headerName: 'Gender',
        order: 5,
        filter: 'agSetColumnFilter',
        cellRenderer: (params: GroupCellRendererParams) => (
          <FlexContainer>
            {params.value && params.value.length ? (
              <Tooltip label={params.value.join(',')}>
                <Text>{params.value.join(', ')}</Text>
              </Tooltip>
            ) : (
              <Text>-</Text>
            )}
          </FlexContainer>
        ),
      },
      {
        field: 'breed',
        headerName: 'Breed',
        filter: 'agSetColumnFilter',
        cellRenderer: (params: GroupCellRendererParams) => {
          const breed = hasValue(params.value)
            ? `${params.value}${' '}
        ${
          params.data.purebred_breed ? `(${params.data.purebred_breed})` : ''
        }${' '}
        ${params.data.mixed_breed ? `(${params.data.mixed_breed})` : ''}`
            : '-';
          return (
            <FlexContainer>
              {params.value ? (
                <Tooltip label={breed}>
                  <Text>{breed}</Text>
                </Tooltip>
              ) : (
                <Text>-</Text>
              )}
            </FlexContainer>
          );
        },
      },
      {
        field: 'date_from',
        headerName: 'Available',
        filter: YearFilter,
        cellRenderer: (params: GroupCellRendererParams) => (
          <FlexContainer>
            {params.value ? (
              <Text>
                {/* date_to is inclusive and always set, so equal bounds are a single date. */}
                {formatListingDateRange(
                  params.value,
                  params.data.date_to === params.value
                    ? null
                    : params.data.date_to
                )}
              </Text>
            ) : (
              <Text>-</Text>
            )}
          </FlexContainer>
        ),
      },
      {
        field: 'prices',
        headerName: 'Price',
        width: 150,
        filter: PriceFilter,
        cellRenderer: (params: GroupCellRendererParams) => {
          if (params.value && Object.keys(params.value).length > 0) {
            return (
              <FlexContainer>
                {priceRenderer(Object.values(params.value)[0] as any)}
              </FlexContainer>
            );
          } else {
            return (
              <FlexContainer>
                <Text>-</Text>
              </FlexContainer>
            );
          }
        },
      },
      {
        field: 'prices',
        headerName: 'Price Type',
        filter: priceTypeFilter,
        filterParams: {
          options: [
            PriceTypeEnum.HSCW,
            PriceTypeEnum.LIVEWEIGHT,
            PriceTypeEnum.PER_HEAD,
            'POA',
          ],
        },
        cellRenderer: (params: GroupCellRendererParams) => {
          const fixedOrNegotiableHandler = (priceLabel: string) => {
            if (priceLabel.includes('Negotiable')) {
              return '(ONO)';
            } else if (priceLabel.includes('Fixed')) {
              return '(Fixed)';
            } else {
              return '';
            }
          };
          if (
            params.value &&
            Object.keys(params.value).length > 0 &&
            params.data.prices_label !== 'POA'
          ) {
            return (
              <FlexContainer>
                <Text>{`${getPriceType(
                  Object.keys(params.value)[0]
                )} ${fixedOrNegotiableHandler(
                  params.data.prices_label
                )}`}</Text>
              </FlexContainer>
            );
          } else if (params.data.prices_label === 'Auction') {
            return (
              <FlexContainer>
                <Text>Auction</Text>
              </FlexContainer>
            );
          } else {
            return (
              <FlexContainer>
                <Text>POA</Text>
              </FlexContainer>
            );
          }
        },
      },
      {
        field: 'sold_price',
        headerName: 'Filled Price',
        filter: PriceFilter,
        cellRenderer: (params: GroupCellRendererParams) => {
          if (params.data.sold && params.data.sold_price) {
            return (
              <FlexContainer>
                <Text>
                  {`${formattedSoldPrices(
                    params.data.sold_price,
                    params.data.sold_price_type
                  )} `}
                  {getPriceType(params.data.sold_price_type)}
                </Text>
              </FlexContainer>
            );
          } else if (params.data.sold && !params.data.sold_price) {
            return (
              <FlexContainer>
                <Text>Price not disclosed</Text>
              </FlexContainer>
            );
          } else {
            return (
              <FlexContainer>
                <Text>-</Text>
              </FlexContainer>
            );
          }
        },
      },
      {
        field: 'weight_expected_min',
        headerName: 'Min',
        filter: 'agNumberColumnFilter',
        filterParams: {
          maxNumConditions: 1,
        },
        cellRenderer: (params: GroupCellRendererParams) =>
          params.value && !Number.isNaN(params.value) ? (
            numberCellRenderer({ ...params, value: params.value }, 'kg')
          ) : (
            <Text>-</Text>
          ),
      },
      {
        field: 'weight_expected_avg',
        headerName: 'Avg',
        filter: 'agNumberColumnFilter',
        filterParams: {
          maxNumConditions: 1,
        },
        cellRenderer: (params: GroupCellRendererParams) =>
          params.value && !Number.isNaN(params.value) ? (
            numberCellRenderer({ ...params, value: params.value }, 'kg')
          ) : (
            <Text>-</Text>
          ),
      },
      {
        field: 'weight_expected_max',
        headerName: 'Max',
        filter: 'agNumberColumnFilter',
        filterParams: {
          maxNumConditions: 1,
        },
        cellRenderer: (params: GroupCellRendererParams) =>
          params.value && !Number.isNaN(params.value) ? (
            numberCellRenderer({ ...params, value: params.value }, 'kg')
          ) : (
            <Text>-</Text>
          ),
      },
      {
        field: 'agent',
        headerName: 'Seller',
        filter: 'agSetColumnFilter',
        cellRenderer: (params: GroupCellRendererParams) => (
          <FlexContainer>
            {params.value ? <Text>{params.value}</Text> : <Text>-</Text>}
          </FlexContainer>
        ),
      },
      {
        field: 'networks',
        headerName: 'Networks',
        width: 250,
        hide: !marketplaceConfig,
        filter: 'agSetColumnFilter',
        cellRenderer: (params: GroupCellRendererParams) => {
          return (
            <FlexContainer>
              {params.value && (
                <Tooltip label={params.value.join(',')}>
                  <Text>{params.value.join(',')}</Text>
                </Tooltip>
              )}
            </FlexContainer>
          );
        },
      },
      {
        field: 'creator',
        headerName: 'Creator',
        hide: !session?.user?.user_id,
        filter: 'agSetColumnFilter',
        cellRenderer: (params: GroupCellRendererParams) => (
          <FlexContainer>
            {params.value ? <Text>{params?.value}</Text> : <Text>-</Text>}
          </FlexContainer>
        ),
      },
      {
        field: 'suburb',
        headerName: 'Town',
        filter: 'agSetColumnFilter',
        cellRenderer: (params: GroupCellRendererParams) =>
          stringCellRenderer(params),
      },
      {
        field: 'state',
        headerName: 'State',
        filter: 'agSetColumnFilter',
        cellRenderer: (params: GroupCellRendererParams) =>
          stringCellRenderer(params),
      },
      {
        field: 'title',
        headerName: 'Title',
        filter: SearchFilter,
        cellRenderer: (params: GroupCellRendererParams) => (
          <Tooltip label={params.value}>
            {linkCellRenderer(params, buildPublicListingPath(params.data))}
          </Tooltip>
        ),
      },
      {
        field: 'media',
        headerName: 'Media',
        cellRenderer: (params: GroupCellRendererParams) =>
          params.value && params.value.length > 0 ? (
            numberCellRenderer({ ...params, value: params.value.length })
          ) : (
            <Text>-</Text>
          ),
      },
      {
        field: 'last_updated',
        headerName: 'Last Updated/ Filled Date',
        cellRenderer: (params: GroupCellRendererParams) =>
          params.value && !params.data.sold ? (
            formatDistanceToNow(new Date(params.value as string), {
              addSuffix: true,
            })
          ) : params.data.sold ? (
            <Tag backgroundColor='red' color='white'>
              Filled:{' '}
              {format(
                new Date(params.data.sold as string),
                DATE_FORMAT.listing
              )}
            </Tag>
          ) : (
            <Text>-</Text>
          ),
      },
    ];

    setColumnDefs([...colData]);
  }, [listingData, isMarketplaceConfigLoading]);

  const mappedData = (data: ListingRead[]) => {
    if (data) {
      return data.map((item) => {
        return {
          ...item,
          ...item.form_data,
          state: item.listing_address?.state,
          suburb: item.listing_address?.suburb,
          weight_expected_min: parseInt(item?.form_data?.weight_expected_min),
          weight_expected_max: parseInt(item?.form_data?.weight_expected_max),
          weight_expected_avg: parseInt(item?.form_data?.weight_expected_avg),
          agent:
            item?.form_data?.agent === 'agent'
              ? 'Agent'
              : item?.form_data?.agent === 'is_agent'
              ? 'Vendor/Agent'
              : item?.form_data?.agent === 'no'
              ? 'Vendor'
              : undefined,
          creator: item.creator
            ? item.creator.firstname + ' ' + item.creator.lastname
            : undefined,
          creatorId: item?.creator?.user_id,
          status: item.sold ? 'Filled' : 'Active',
          networks:
            item.networks && !!item.networks.length
              ? getListOfNetworks({ networks: item.networks }).map(
                  (network) => network.name
                )
              : undefined,
          stock_gender:
            item.product === ProductTypes.Ct
              ? getArrayItem(item?.form_data?.cattle_gender)
              : item.product === ProductTypes.Gt
              ? getArrayItem(item?.form_data?.goat_gender)
              : getArrayItem(item?.form_data?.sheep_gender),
          stock_ages:
            item.product === ProductTypes.Ct
              ? getArrayItem(item?.form_data?.cattle_stock_ages)
              : item.product === ProductTypes.Gt
              ? getArrayItem(item?.form_data?.goat_stock_ages)
              : getArrayItem(item?.form_data?.sheep_stock_ages),
        };
      });
    } else {
      return [];
    }
  };

  const handleListingView = (listing: ListingRead) => {
    router.push(buildPublicListingPath(listing));
  };

  const autoSizeStrategy = useMemo(() => {
    return {
      type: 'fitCellContents',
    };
  }, []);

  return (
    <GridWrapper className='ag-theme-alpine'>
      <AgGridReact
        domLayout='autoHeight'
        columnDefs={columnDefs}
        rowData={mappedData(listingData)}
        paginationPageSize={perPage}
        pagination={true}
        reactiveCustomComponents
        overlayNoRowsTemplate={noRowsTemplate}
        overlayLoadingTemplate={loadingTemplate}
        onCellClicked={(event: CellClickedEvent) =>
          event.colDef.field !== 'bookmark_status' &&
          handleListingView(event.data)
        }
        defaultColDef={{
          suppressMenu: true,
          sortable: false,
          suppressMovable: true,
          maxWidth: 200,
        }}
        autoSizeStrategy={autoSizeStrategy as SizeColumnsToFitGridStrategy}
        suppressColumnVirtualisation={true}
      ></AgGridReact>
      <CreateListingDialog
        isOpen={createListingDialogOpen}
        onDismiss={closeCreateListingDialog}
      />
    </GridWrapper>
  );
};

export default MarketplaceTableView;
